/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('./bootstrap');

import './plugins/compat-config';
import 'vuejs-dialog/dist/vuejs-dialog.min.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
// import 'vue-loading-overlay/dist/vue-loading.css';
import 'vue-select/dist/vue-select.css';

import Vue from 'vue';
import VuejsDialog from 'vuejs-dialog';
import Vuelidate from 'vuelidate';
import { BootstrapVue } from 'bootstrap-vue';
import vSelect from 'vue-select';
import { createI18n } from 'vue-i18n';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';

import { setupVueI18n } from './caregiver/virtual-interview/shared-utils';

import Loading from './plugins/loading-overlay';

// only enable bugsnag if a key is present
if (process.env.MIX_BUGSNAG_API_KEY) {
    const bugsnagClient = Bugsnag.start({
        apiKey: process.env.MIX_BUGSNAG_API_KEY,
        releaseStage: process.env.NODE_ENV,
        plugins: [new BugsnagPluginVue()]
    });

    const bugsnagVue = bugsnagClient.getPlugin('vue');

    bugsnagVue.installVueErrorHandler(Vue);

    window.bugsnagClient = bugsnagClient;
}

Vue.use(VuejsDialog);

// vuejs-dialog's own install() sets `Vue.prototype.$dialog = ...` (the
// Vue 2 idiom) — like $eventHub and $loading above/below, this does NOT
// propagate to component instances under @vue/compat. Confirmed in
// production: components rendered as dialog *content* (e.g.
// Files/List.vue, itself opened via `Vue.dialog.registerComponent`)
// could reach $eventHub/$loading (already mixin-based) but not $dialog,
// throwing "Cannot read properties of undefined (reading 'confirm')".
// Capture the instance vuejs-dialog attached to the prototype and
// re-expose it the same compat-safe way.
const dialogPlugin = Vue.prototype.$dialog;

Vue.mixin({
    beforeCreate() {
        this.$dialog = dialogPlugin;
    },
});

Vue.use(Vuelidate);
Vue.use(BootstrapVue);
Vue.use(Loading);

// VueClipboard.config.autoSetContainer = true;
// // Vue.config.silent = true;

// Vue.use(VueClipboard);

// Global event bus. NOTE: `Vue.prototype.$eventHub = new Vue()` (the Vue 2
// idiom) does NOT propagate to component instances under @vue/compat —
// verified by regression test in __tests__/eventhub-compat.test.js. A
// global mixin's beforeCreate hook is the compat-safe equivalent.
const eventHub = new Vue();

Vue.mixin({
    beforeCreate() {
        this.$eventHub = eventHub;
    },
});

Vue.mixin({
    methods: {
        $copyText(text, container) {
            return new Promise((resolve, reject) => {
                const containerEl =
                    (typeof container === 'string'
                        ? document.getElementById(container)
                        : container) || document.body;

                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                containerEl.appendChild(textarea);
                textarea.focus();
                textarea.select();

                try {
                    const successful = document.execCommand('copy');
                    containerEl.removeChild(textarea);
                    successful
                        ? resolve(text)
                        : reject(new Error('Copy command was unsuccessful'));
                } catch (err) {
                    containerEl.removeChild(textarea);
                    reject(err);
                }
            });
        },
    },
});

window.Vue = Vue;

// Misc. common components
Vue.component(
    'SelectableAdd',
    require('./components/SelectableAdd.vue').default,
);
Vue.component('SearchForm', require('./components/Search.vue').default);
Vue.component('Pagination', require('./components/Pagination.vue').default);
Vue.component('VSelect', vSelect);
Vue.component(
    'LetterSearchFilter',
    require('./components/LetterSearchFilter.vue').default,
);
Vue.component('SelectFilter', require('./components/SelectAndGo.vue').default);
Vue.component('LoadingIcon', require('./components/LoadingIcon.vue').default);
// Vue.component('ConfirmOTP', require('./components/ConfirmOTP.vue').default);

// Activation Button
Vue.component('ActivationButton', () =>
    import(
        /* webpackChunkName: "activation-button" */ './components/ActivationButton.vue'
    ),
);

// Status Icon
Vue.component('StatusIcon', () =>
    import(/* webpackChunkName: "status-icon" */ './components/StatusIcon.vue'),
);

// PDF Viewer
Vue.component('PdfViewer', () =>
    import(/* webpackChunkName: "pdf-viewer" */ './components/PDFViewer.vue'),
);

// Calendar
Vue.component('VirtualCalendar', () =>
    import(
        /* webpackChunkName: "virtual-calendar" */ './components/Calendar.vue'
    ),
);

Vue.component('EntityFilesButton', () =>
    import(
        /* webpackChunkName: "entity-files-button" */ './components/Files/ListButton.vue'
    ),
);
Vue.component('EntityFilesList', () =>
    import(
        /* webpackChunkName: "entity-files-list" */ './components/Files/List.vue'
    ),
);

//Employees
Vue.component('EditEmployee', () =>
    import(
        /* webpackChunkName: "edit-employee" */ './components/Employees/Edit.vue'
    ),
);
Vue.component('AddEmployee', () =>
    import(
        /* webpackChunkName: "add-employee" */ './components/Employees/Add.vue'
    ),
);
Vue.component('EmployeeStatusActions', () =>
    import(
        /* webpackChunkName: "employee-status-actions" */ './components/Employees/StatusActions.vue'
    ),
);

// Users
Vue.component('UserForm', () =>
    import(/* webpackChunkName: "user-form" */ './components/Users/Form.vue'),
);
Vue.component('SuspendUser', () =>
    import(
        /* webpackChunkName: "suspend-user" */ './components/Users/Suspend.vue'
    ),
);
Vue.component('ShadowUser', () =>
    import(
        /* webpackChunkName: "shadow-user" */ './components/Users/Shadow.vue'
    ),
);

// Locations
Vue.component('LocationForm', () =>
    import(
        /* webpackChunkName: "location-form" */ './components/Locations/Form.vue'
    ),
);

// Incidents
Vue.component('IncidentForm', () =>
    import(
        /* webpackChunkName: "incident-form" */ './components/Incidents/Form.vue'
    ),
);
Vue.component('PrintIncident', () =>
    import(
        /* webpackChunkName: "print-incident" */ './components/Incidents/Print.vue'
    ),
);
// Vue.component('IncidentExtension', () =>
//     import(
//         /* webpackChunkName: "incident-extension" */ './components/Incidents/Extension.vue'
//     ),
// );
Vue.component('IncidentFollowup', () =>
    import(
        /* webpackChunkName: "incident-followup" */ './components/Incidents/Followup.vue'
    ),
);

// Agencies
Vue.component('AddAgency', () =>
    import(
        /* webpackChunkName: "add-agency" */ './components/Agencies/Add.vue'
    ),
);
Vue.component('EditAgency', () =>
    import(
        /* webpackChunkName: "edit-agency" */ './components/Agencies/Edit.vue'
    ),
);

// Incident Types
Vue.component('IncidentTypeForm', () =>
    import(
        /* webpackChunkName: "incident-type-form" */ './components/IncidentTypes/Form.vue'
    ),
);

// Event Types
Vue.component('EventTypeForm', () =>
    import(
        /* webpackChunkName: "event-type-form" */ './components/EventTypes/Form.vue'
    ),
);

// MCO
Vue.component('AddMco', () =>
    import(/* webpackChunkName: "add-mco-form" */ './components/MCOs/Add.vue'),
);
Vue.component('EditMco', () =>
    import(
        /* webpackChunkName: "edit-mco-form" */ './components/MCOs/Edit.vue'
    ),
);

// Waiver Types
Vue.component('WaiverTypeForm', () =>
    import(
        /* webpackChunkName: "waiver-type-form" */ './components/WaiverTypes/Form.vue'
    ),
);

// Clients
Vue.component('ClientForm', () =>
    import(
        /* webpackChunkName: "client-form" */ './components/Clients/Form.vue'
    ),
);
Vue.component('ClientIncidents', () =>
    import(
        /* webpackChunkName: "client-incidents" */ './components/Clients/Incidents.vue'
    ),
);
Vue.component('ClientNotifier', () =>
    import(
        /* webpackChunkName: "client-notifier" */ './components/Clients/Autocaller.vue'
    ),
);
Vue.component('ClientAssessments', () =>
    import(
        /* webpackChunkName: "client-assessments" */ './components/Assessments/List.vue'
    ),
);
Vue.component('ClientCheckIn', () =>
    import(
        /* webpackChunkName: "client-checkin" */ './components/Clients/Checkin.vue'
    ),
);
// Vue.component('IncidentsFollowUpList', () =>
//     import(
//         /* webpackChunkName: "incidents-follow-up-list" */ './components/Clients/IncidentsFollowUpList.vue'
//     ),
// );
Vue.component('ClientSupervisionFiles', () =>
    import(
        /* webpackChunkName: "client-supervision-files" */ './components/Clients/ListSupervisionFiles.vue'
    ),
);

// Portal Navigation
Vue.component('PortalNavigationForm', () =>
    import(
        /* webpackChunkName: "portal-navigation-form" */ './components/PortalNavigation/Form.vue'
    ),
);
Vue.component('DeleteNavigationButton', () =>
    import(
        /* webpackChunkName: "delete-navigation-button" */ './components/PortalNavigation/DeleteButton.vue'
    ),
);

// Companies
Vue.component('CompanyForm', () =>
    import(
        /* webpackChunkName: "company-form" */ './components/Companies/Form.vue'
    ),
);

// Change Password
Vue.component('ChangePasswordForm', () =>
    import(
        /* webpackChunkName: "change-password-form" */ './components/Auth/ChangePassword.vue'
    ),
);

// Applications
Vue.component('AdminCaregiverApplicationDetails', () => {
    return import(
        /* webpackChunkName: "admin-caregiver-application-details" */ './components/Admin/Applications/Applicant.vue'
    );
});

// Assessment Survey
Vue.component('ClientAssessmentSurveyWizard', () =>
    import(
        /* webpackChunkName: "assessment-survey-wizard" */ './components/Assessments/Survey.vue'
    ),
);

// Flu Report
Vue.component('DcwFluReport', () =>
    import(
        /* webpackChunkName: "flu-report-generator" */ './components/Employees/DcwFluReport.vue'
    ),
);

// Files Report
Vue.component('EmployeeFilesReport', () =>
    import(
        /* webpackChunkName: "files-report-generator" */ './components/Employees/FilesReport.vue'
    ),
);

// Video Assessment
Vue.component('VideoInvite', () =>
    import(
        /* webpackChunkName: "video-invite" */ './components/TwoWayVideo/Invite.vue'
    ),
);
Vue.component('VideoCall', () =>
    import(
        /* webpackChunkName: "video-call" */ './components/TwoWayVideo/Live.vue'
    ),
);

Vue.component('UpdateAccountModal', () =>
    import(
        /* webpackChunkName: "update-account-modal" */ './components/UpdateAccount.vue'
    ),
);

// 2fa
Vue.component('SendTwoFactorCodeForm', () =>
    import(
        /* webpackChunkName: "send-two-factor-code-form" */ './components/TwoFactor/Send.vue'
    ),
);
Vue.component('VerifyTwoFactorCodeForm', () =>
    import(
        /* webpackChunkName: "verify-two-factor-code-form" */ './components/TwoFactor/Verify.vue'
    ),
);
Vue.component('ResendOtpCodeButton', () =>
    import(
        /* webpackChunkName: "resend-otp-code-button" */ './components/TwoFactor/ResendButton.vue'
    ),
);

// User Roles
Vue.component('RoleForm', () =>
    import(
        /* webpackChunkName: "role-form" */ './components/Roles/Form.vue'
    ),
);

// ReAssignLocation Button
Vue.component('ReassignApplicantLocationButton', () =>
    import(
        /* webpackChunkName: "reassign-applicant-location-button" */ './components/Admin/Applications/ReassignApplicantLocationButton.vue'
    ),
);

// Secure Shares
Vue.component('SecureShareForm', () =>
    import(
        /* webpackChunkName: "secure-share-form" */ './components/SecureShares/Form.vue'
    ),
);
Vue.component('SecureShareActions', () =>
    import(
        /* webpackChunkName: "secure-share-actions" */ './components/SecureShares/Actions.vue'
    ),
);
Vue.component('SecureShareAccess', () =>
    import(
        /* webpackChunkName: "secure-share-access" */ './components/SecureShares/Access.vue'
    ),
);

// Leads
Vue.component('LeadsForm', () =>
    import(
        /* webpackChunkName: "leads-form" */ './components/Leads/Form.vue'
    ),
);
Vue.component('LeadsTemplateForm', () =>
    import(
        /* webpackChunkName: "leads-template-form" */ './components/Leads/Template.vue'
    ),
);
Vue.component('LeadsNotes', () =>
    import(
        /* webpackChunkName: "leads-notes" */ './components/Leads/Notes.vue'
    ),
);
Vue.component('LeadsCorrespondences', () =>
    import(
        /* webpackChunkName: "leads-correspondences" */ './components/Leads/Correspondences.vue'
    ),
);
Vue.component('LeadsTeam', () =>
    import(
        /* webpackChunkName: "leads-team" */ './components/Leads/Teams.vue'
    ),
);
Vue.component('LeadsTasks', () =>
    import(
        /* webpackChunkName: "leads-tasks" */ './components/Leads/Tasks.vue'
    ),
);

// Client Satisfaction Report
Vue.component('ClientSatisfactionReport', () =>
    import(
        /* webpackChunkName: "satisfaction-report-generator" */ './components/Clients/SatisfactionReport.vue'
    ),
);

// Client Satisfaction Report
Vue.component('ClientSatisfactionReport', () =>
    import(
        /* webpackChunkName: "satisfaction-report-generator" */ './components/Clients/SatisfactionReport.vue'
    ),
);

// Client Payment Agreement Form
Vue.component('SendClientPaymentAgreementForm', () =>
    import(
        /* webpackChunkName: "send-client-payment-agreement-form" */ './components/Clients/SendPaymentAgreement.vue'
    ),
);

Vue.component('ClientPaymentAgreementForm', () =>
    import(
        /* webpackChunkName: "client-payment-agreement-form" */ './components/Clients/PaymentAgreementForm.vue'
    ),
);

// Client Assessment Report
Vue.component('ClientAssessmentReport', () =>
    import(
        /* webpackChunkName: "assessment-report-generator" */ './components/Assessments/Report.vue'
    ),
);

// Client Assessment Report List - Dashboard
Vue.component('ClientAssessmentReportList', () =>
    import(
        /* webpackChunkName: "client-assessment-report-list" */ './components/Dashboard/ClientAssessmentReportList.vue'
    ),
);

// SortDataIcon
Vue.component('SortDataIcon', () =>
    import(
        /* webpackChunkName: "sort-data-icon" */ './components/SortDataIcon.vue'
    ),
);

// EmployeeDocumentChecklist
Vue.component('EmployeeDocumentChecklist', () =>
    import(
        /* webpackChunkName: "employee-document-checklist" */ './components/Employees/DocumentChecklist.vue'
    ),
);

// IncidentComments
Vue.component('IncidentComments', () =>
    import(
        /* webpackChunkName: "incident-comments" */ './components/Incidents/Comments.vue'
    ),
);

// Virtual Interview Questionnaire Responses
Vue.component('VirtualInterviewQuestionnaireResponses', () =>
    import(
        /* webpackChunkName: "virtual-interview-questionnaire-responses" */ './components/Admin/Applications/QuestionnaireResponses.vue'
    ),
);

// Hold Button
Vue.component('HoldButton', () =>
    import(
        /* webpackChunkName: "hold-button" */ './components/HoldButton.vue'
    ),
);

// Hold Status Icon
Vue.component('HoldStatusIcon', () =>
    import(
        /* webpackChunkName: "hold-status-icon" */ './components/HoldStatusIcon.vue'
    ),
);

Vue.component('ClientPopupVisit', () =>
    import(
        /* webpackChunkName: "client-popup-visit" */ './components/Clients/PopupVisit.vue'
    ),
);

Vue.component('DateInput', () =>
    import(
        /* webpackChunkName: "date-input" */ './components/DateInput.vue'
    ),
);



// Virtual Interview Live Invite
// Vue.component('VirtualInterviewLiveInvite', () =>
//     import(
//         /* webpackChunkName: "virtual-interview-invite" */ './caregiver/virtual-interview/Invite.vue'
//     ),
// );

// Library Category Form
Vue.component('LibraryCategoryForm', () =>
    import(
        /* webpackChunkName: "library-category-form" */ './components/Library/CategoryForm.vue'
    ),
);
// Library Item Form
Vue.component('LibraryItemForm', () =>
    import(
        /* webpackChunkName: "library-item-form" */ './components/Library/ItemForm.vue'
    ),
);
// Library Mass Upload
Vue.component('LibraryMassUpload', () =>
    import(
        /* webpackChunkName: "library-mass-upload" */ './components/Library/MassUpload.vue'
    ),
);

// Access Token
Vue.component('AccessTokenForm', () =>
    import(/* webpackChunkName: "access-token-form" */ './components/Users/AccessTokenForm.vue'),
);
Vue.component('RevokeAccessToken', () =>
    import(/* webpackChunkName: "revoke-access-token" */ './components/Users/RevokeAccessToken.vue'),
);

// TimeOff Requests Details
Vue.component('TimeOffRequestDetails', () =>
    import(/* webpackChunkName: "time-off-request-details" */ './components/Employees/TimeOffs/Details.vue'),
);

// print page
Vue.component('PrintPage', () =>
    import(/* webpackChunkName: "print-page" */ './components/PrintPage.vue'),
);

// download client files
Vue.component('DownloadClientFilesButton', () =>
    import(/* webpackChunkName: "download-client-files-button" */ './components/Clients/DownloadFiles.vue'),
);

// business licenses
Vue.component('BusinessLicenseForm', () =>
    import(/* webpackChunkName: "business-license-form" */ './components/BusinessLicenses/Form.vue'),
);
Vue.component('RemoveBusinessLicenseButton', () =>
    import(/* webpackChunkName: "remove-business-license-button" */ './components/BusinessLicenses/Remove.vue'),
);
Vue.component('BusinessLicenseCategoryForm', () =>
    import(/* webpackChunkName: "business-license-category-form" */ './components/BusinessLicenses/CategoryForm.vue'),
);

// training resources
Vue.component('TrainingResourceForm', () =>
    import(/* webpackChunkName: "training-resource-form" */ './components/TrainingResources/Form.vue'),
);
// training resources
Vue.component('TrainingVideoPlayer', () =>
    import(/* webpackChunkName: "training-video-player" */ './components/TrainingResources/Player.vue'),
);
Vue.component('RemoveTrainingResourceButton', () =>
    import(/* webpackChunkName: "remove-training-resource-button" */ './components/TrainingResources/Remove.vue'),
);
Vue.component('TrainingProgress', () =>
    import(/* webpackChunkName: "training-progress" */ './components/TrainingResources/Progress.vue'),
);

Vue.component('EmployeeTwoWeeksCheckIn', () =>
    import(/* webpackChunkName: "employee-two-weeks-check-in" */ './components/Employees/TwoWeeksCheckIn.vue'),
);

const { i18nConfig } = setupVueI18n();

const i18n = createI18n({
    legacy: true,
    ...i18nConfig,
});

Vue.use(i18n);

const app = new Vue({
    el: '#app',
    beforeCreate() {
        
    }
});
